import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal, Input, Button, Typography, Spin } from 'antd';
import { RobotOutlined, SendOutlined, WarningOutlined } from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS } from '@/constants';
import type { ApiResponse, AiChatResponse } from '@/types';

const { Text } = Typography;

const MAX_MESSAGES_PER_CARD = 3;

interface ChatMessage {
  role: 'user' | 'ai';
  content: string;
}

interface AiChatModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string;
  sessionId: string | null;
  cardTitle?: string;
}

export default function AiChatModal({ open, onClose, cardId, sessionId, cardTitle }: AiChatModalProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [remainingToday, setRemainingToday] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const userMessageCount = messages.filter((m) => m.role === 'user').length;
  const perCardLimitReached = userMessageCount >= MAX_MESSAGES_PER_CARD;
  const dailyLimitReached = remainingToday === 0;
  const inputDisabled = perCardLimitReached || dailyLimitReached;

  const chatMutation = useMutation({
    mutationFn: (question: string) =>
      api.post<never, ApiResponse<AiChatResponse>>(API_ENDPOINTS.AI_CHAT, {
        cardId,
        sessionId: sessionId ?? undefined,
        question,
      }),
    onSuccess: (res) => {
      const data = res.data;
      if (data.filterResult === 'RATE_LIMITED') {
        setRemainingToday(0);
        setMessages((prev) => [
          ...prev,
          { role: 'ai', content: "You've used all your AI questions for today! Ask your teacher if you need more help." },
        ]);
      } else {
        setRemainingToday(data.remainingToday);
        setMessages((prev) => [...prev, { role: 'ai', content: data.response }]);
      }
    },
    onError: (error: { error?: { code?: string } }) => {
      const code = error?.error?.code;
      const msg = code === 'AI_TUTOR_DISABLED'
        ? 'AI tutor is not available right now.'
        : code === 'NOT_ASSIGNED'
          ? 'You are not assigned to this deck.'
          : 'Oops! Something went wrong. Try again in a moment.';
      setMessages((prev) => [...prev, { role: 'ai', content: msg }]);
    },
  });

  const handleSend = () => {
    const question = inputValue.trim();
    if (!question || inputDisabled || chatMutation.isPending) return;

    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setInputValue('');
    chatMutation.mutate(question);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInputValue('');
      setRemainingToday(null);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      styles={{ body: { padding: 0 } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
          <div
            style={{
              width: 32, height: 32, borderRadius: 8, background: '#ede9fe',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <RobotOutlined style={{ fontSize: 18, color: '#6366f1' }} />
          </div>
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: 15 }}>AI Tutor</Text>
            {cardTitle && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: 1.2 }}>
                {cardTitle}
              </Text>
            )}
          </div>
          {remainingToday !== null && remainingToday <= 3 && remainingToday > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fef3c7', borderRadius: 6, padding: '3px 8px' }}>
              <WarningOutlined style={{ color: '#d97706', fontSize: 12 }} />
              <Text style={{ fontSize: 11, color: '#92400e', fontWeight: 600 }}>
                {remainingToday} left today
              </Text>
            </div>
          )}
        </div>
      }
    >
      <div
        style={{
          minHeight: 200, maxHeight: 360, overflowY: 'auto', padding: '16px 20px',
          display: 'flex', flexDirection: 'column', gap: 12,
          background: '#fafafa',
        }}
      >
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <RobotOutlined style={{ fontSize: 32, color: '#c7d2fe', marginBottom: 8 }} />
            <Text type="secondary" style={{ display: 'block', fontSize: 14 }}>
              Ask me anything about this card!
            </Text>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
              {MAX_MESSAGES_PER_CARD} questions per card
            </Text>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div
              style={{
                maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
                background: msg.role === 'user' ? '#ede9fe' : '#f0fdf4',
                color: msg.role === 'user' ? '#5b21b6' : '#166534',
                fontSize: 14, lineHeight: 1.5,
                borderBottomRightRadius: msg.role === 'user' ? 4 : 14,
                borderBottomLeftRadius: msg.role === 'ai' ? 4 : 14,
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {chatMutation.isPending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <Spin size="small" />
            <Text type="secondary" style={{ fontSize: 13 }}>Thinking...</Text>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {userMessageCount > 0 && (
        <div style={{ textAlign: 'center', padding: '4px 0', background: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {userMessageCount} of {MAX_MESSAGES_PER_CARD} questions used on this card
          </Text>
        </div>
      )}

      <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        {inputDisabled ? (
          <div style={{ flex: 1, textAlign: 'center', padding: '8px 0' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {dailyLimitReached
                ? "You've used all your AI questions for today."
                : `You've used all ${MAX_MESSAGES_PER_CARD} questions on this card.`}
            </Text>
          </div>
        ) : (
          <>
            <Input.TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this card..."
              autoSize={{ minRows: 1, maxRows: 3 }}
              maxLength={500}
              disabled={chatMutation.isPending}
              style={{ borderRadius: 12 }}
              autoFocus
            />
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!inputValue.trim() || chatMutation.isPending}
              loading={chatMutation.isPending}
              style={{ background: '#6366f1', borderColor: '#6366f1', flexShrink: 0 }}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
